import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { AuthService } from "./auth.service";
import { ServiceOptions } from "../models/service-options";
import { ServiceCallInfo } from "../models/common";
import {
  DistributionWaybillsResponse,
  DistributionWaybillsSearchParams,
  CreateDeliveryWaybillsResponse,
} from "../models/delivery.model";
import { Store } from "@ngrx/store";
import { GET_DISTRIBUTION_WAYBILLS } from "../ngrx/ngrx-actions";
import { ServiceMetaData } from "../models/service-meta.model";

@Injectable({
  providedIn: "root",
})
export class DeliveryService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private readonly store: Store
  ) { }

  getDistributionWaybills(
    searchParams: DistributionWaybillsSearchParams
  ): Observable<DistributionWaybillsResponse> {
    let response = new Observable<DistributionWaybillsResponse>();
    let serviceName = "get_distribution_waybills";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) =>
          this.extractDistributionWaybills(data, searchParams, info)
        ),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractDistributionWaybills(
    res: any,
    searchParams: DistributionWaybillsSearchParams,
    info: ServiceCallInfo
  ): DistributionWaybillsResponse {
    this.authService.extractRequestStatusNew(info, res);
    let result = new DistributionWaybillsResponse();
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data) {
      if (data.metadata) {
        metaData.totalRecords = data.metadata.totRec;
        metaData.fromRecord = data.metadata.fromRec;
        metaData.toRecord = data.metadata.toRec;
        metaData.searchId = data.metadata.searchId;
      } else {
        metaData.fromRecord = searchParams.rangeFrom;
        metaData.toRecord = searchParams.rangeTo;
        metaData.searchId = searchParams.searchId;
      }

      if (data.waybills && Array.isArray(data.waybills)) {
        result.waybills = data.waybills.map((w: any) => ({
          displayType: w.displayType || "",
          clinicId: w.clinicId || 0,
          clinicDescr: w.clinicDescr || "",
          delivererCode: w.delivererCode,
          printedStatus: w.printedStatus,
          orderIdMmd: w.orders && w.orders.length > 0 ? w.orders[0]?.orderIdMmd : null,
          orderId: w.orders && w.orders.length > 0 ? w.orders[0]?.orderId : null,
          patientName: w.orders && w.orders.length > 0 ? w.orders[0]?.patientName : null,
          printedSw: w.printedSw,
        }));
      }
    }

    this.store.dispatch(
      GET_DISTRIBUTION_WAYBILLS({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  createDeliveryWaybills(
    manualOrders: string[],
    delivererCode: string = undefined
  ): Observable<CreateDeliveryWaybillsResponse> {
    let response = new Observable<CreateDeliveryWaybillsResponse>();
    let serviceName = "brosh.CreateDeliveryWaybills";
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params["orderIdsMmd"] = manualOrders;
    if (delivererCode) params["delivererCode"] = delivererCode;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, true);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractCreateDeliveryWaybills(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractCreateDeliveryWaybills(
    res: any,
    info: ServiceCallInfo
  ): CreateDeliveryWaybillsResponse {
    this.authService.extractRequestStatusNew(info, res);
    let result = new CreateDeliveryWaybillsResponse();
    let data = res.reply.data;

    if (data) {
      result.printedClinicCnt = data.printedClinicCnt || 0;
      result.printedOrderCnt = data.printedOrderCnt || 0;
      result.waybillsPdfUrl = data.waybillsPdfUrl || "";
    }

    return result;
  }
}
